if (!customElements.get("product-bundle-configurator")) {
  customElements.define(
    "product-bundle-configurator",
    class ProductBundleConfigurator extends HTMLElement {
      connectedCallback() {
        this.requiredQuantity = Number(this.dataset.requiredQuantity || 1);
        this.parentVariantId = Number(this.dataset.parentVariantId || 0);
        this.parentPrice = Number(this.dataset.parentPrice || 0);
        this.parentHandle = this.dataset.parentHandle || "";
        this.parentTitle = this.dataset.parentTitle || "";
        this.mode = this.dataset.mode || "duo";
        this.cartDrawer = document.querySelector("cart-drawer");
        this.mainCart = document.querySelector("cart-items");
        this.submitButton = this.querySelector("[data-add-bundle]");
        this.totalEl = this.querySelector("[data-total-price]");
        this.requirementEl = this.querySelector("[data-requirement-message]");
        this.errorWrapper = this.querySelector(".product-bundle-configurator__error");

        try {
          this.strings = JSON.parse(
            this.querySelector("[data-bundle-strings]")?.textContent || "{}"
          );
        } catch (e) {
          this.strings = {};
        }

        document.body.classList.add("product-bundle-mode");
        const productInfo = this.closest("product-info");
        if (productInfo) productInfo.setAttribute("data-bundle-mode", "true");

        this.addEventListener("click", this.onClick.bind(this));
        this.addEventListener("change", this.onQtyChange.bind(this));
        this.addEventListener("input", this.onQtyChange.bind(this));

        this.updateState();
      }

      disconnectedCallback() {
        document.body.classList.remove("product-bundle-mode");
      }

      onClick(event) {
        const minus = event.target.closest("[data-qty-minus]");
        const plus = event.target.closest("[data-qty-plus]");
        const add = event.target.closest("[data-add-bundle]");

        if (minus || plus) {
          const row = event.target.closest(".product-bundle-configurator__row");
          if (!row || row.dataset.locked === "true") return;
          const input = row.querySelector("[data-qty]");
          if (!input || input.disabled) return;
          const max = Number(row.dataset.max || input.max || 99);
          let value = Number(input.value || 0);
          value = minus ? value - 1 : value + 1;
          input.value = Math.min(Math.max(value, 0), max);
          this.updateState();
          return;
        }

        if (add) {
          this.handleAdd(event);
        }
      }

      onQtyChange(event) {
        if (!event.target.matches("[data-qty]")) return;
        const row = event.target.closest(".product-bundle-configurator__row");
        if (!row || row.dataset.locked === "true") return;
        const max = Number(row.dataset.max || event.target.max || 99);
        let value = Number(event.target.value || 0);
        if (Number.isNaN(value)) value = 0;
        event.target.value = Math.min(Math.max(value, 0), max);
        this.updateState();
      }

      getRows() {
        return Array.from(
          this.querySelectorAll(".product-bundle-configurator__row")
        );
      }

      getSelectableRows() {
        return this.getRows().filter((row) => row.dataset.locked !== "true");
      }

      getSelectableQty() {
        return this.getSelectableRows().reduce((sum, row) => {
          const qty = Number(row.querySelector("[data-qty]")?.value || 0);
          return sum + qty;
        }, 0);
      }

      shortName(row) {
        return (
          row.dataset.scentName ||
          (row.dataset.scentTitle || "").split("•")[0].trim() ||
          "Item"
        );
      }

      /** Expand selectable rows into ordered units */
      getUnits() {
        const units = [];
        this.getSelectableRows().forEach((row) => {
          const qty = Number(row.querySelector("[data-qty]")?.value || 0);
          if (qty < 1) return;
          for (let i = 0; i < qty; i += 1) {
            units.push({
              variantId: Number(row.dataset.variantId),
              productId: row.dataset.productId || "",
              price: Number(row.dataset.price || 0),
              available: row.dataset.available === "true",
              name: this.shortName(row),
              fullTitle: row.dataset.scentTitle || "",
            });
          }
        });
        return units;
      }

      isDuoMode() {
        return this.mode === "duo" || this.requiredQuantity <= 1;
      }

      getPurchaseQty() {
        const selectableQty = this.getSelectableQty();
        if (selectableQty < this.requiredQuantity) return 0;
        // Duo: one parent (includes required jars); Tube set: one parent per complete set
        if (this.isDuoMode()) {
          return 1;
        }
        return Math.floor(selectableQty / this.requiredQuantity);
      }

      /** Units covered by the parent bundle vs remainder billed as individual products */
      splitSetAndExtras() {
        const units = this.getUnits();
        const setUnitCount = this.getPurchaseQty() * this.requiredQuantity;
        return {
          setUnits: units.slice(0, setUnitCount),
          extraUnits: units.slice(setUnitCount),
        };
      }

      visualUnitPrice(cents) {
        const discount = Number(this.dataset.discountPercent || 0);
        if (!(discount > 0) || discount >= 100) return cents;
        return Math.round((cents * (100 - discount)) / 100);
      }

      calculateTotal() {
        const purchaseQty = this.getPurchaseQty();
        const { extraUnits } = this.splitSetAndExtras();
        const extrasTotal = extraUnits.reduce(
          (sum, unit) => sum + this.visualUnitPrice(unit.price || 0),
          0
        );
        return this.parentPrice * purchaseQty + extrasTotal;
      }

      hasUnavailableSelection() {
        return this.getUnits().some((unit) => !unit.available);
      }

      updateState() {
        const selectableQty = this.getSelectableQty();
        const purchaseQty = this.getPurchaseQty();
        const meetsRequirement = selectableQty >= this.requiredQuantity;
        const canAdd =
          meetsRequirement &&
          purchaseQty > 0 &&
          this.parentVariantId > 0 &&
          !this.hasUnavailableSelection();

        if (this.totalEl && typeof formatMoney === "function") {
          this.totalEl.innerHTML = formatMoney(
            this.calculateTotal(),
            window.FoxThemeSettings.money_format
          );
        }

        if (this.requirementEl) {
          if (!meetsRequirement && this.strings.requirement) {
            this.requirementEl.hidden = false;
            this.requirementEl.textContent = this.strings.requirement.replace(
              /\{\{\s*count\s*\}\}/g,
              String(this.requiredQuantity)
            );
          } else {
            this.requirementEl.hidden = true;
            this.requirementEl.textContent = "";
          }
        }

        if (this.submitButton) {
          this.submitButton.disabled = !canAdd;
        }
      }

      aggregateByName(units) {
        const map = new Map();
        units.forEach((unit) => {
          map.set(unit.name, (map.get(unit.name) || 0) + 1);
        });
        return map;
      }

      buildCartItems() {
        const items = [];
        const units = this.getUnits();
        const purchaseQty = this.getPurchaseQty();
        if (purchaseQty < 1 || units.length < this.requiredQuantity) return items;

        const isDuo = this.isDuoMode();
        const jarScentLabel = this.strings.jar_scent_label || "Jar Scent";
        const tubeScentLabel = this.strings.scent_label || "Tube Scent";
        const { setUnits, extraUnits } = this.splitSetAndExtras();

        const properties = {
          _bundle: "true",
          _bundle_parent: this.parentHandle,
          _bundle_type: isDuo ? "duo" : "tube_set",
        };

        // Only included set units stay as parent line properties
        this.aggregateByName(setUnits).forEach((qty, name) => {
          const label = isDuo ? jarScentLabel : tubeScentLabel;
          properties[`${label}: ${name}`] = String(qty);
        });

        items.push({
          id: this.parentVariantId,
          quantity: purchaseQty,
          properties,
        });

        // Remainder over required_quantity → individual products with bundle discount
        if (extraUnits.length) {
          const extrasByVariant = new Map();
          extraUnits.forEach((unit) => {
            const current = extrasByVariant.get(unit.variantId) || {
              qty: 0,
              name: unit.name,
            };
            current.qty += 1;
            extrasByVariant.set(unit.variantId, current);
          });

          extrasByVariant.forEach(({ qty }, variantId) => {
            items.push({
              id: variantId,
              quantity: qty,
              properties: {
                _bundle: "true",
                _bundle_parent: this.parentHandle,
                _bundle_type: isDuo ? "jar_extra" : "tube_extra",
              },
            });
          });
        }

        return items;
      }

      handleAdd(event) {
        event.preventDefault();
        if (!this.submitButton || this.submitButton.disabled) return;

        const items = this.buildCartItems();
        if (!items.length) return;

        let sectionToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:grouped-sections", {
            bubbles: true,
            detail: { sections: sectionToBundle },
          })
        );

        if (this.cartDrawer) {
          sectionToBundle = [
            ...sectionToBundle,
            ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
          ];
        }

        if (this.mainCart) {
          sectionToBundle = [
            ...sectionToBundle,
            ...this.mainCart
              .getSectionsToRender()
              .map((section) => section.section),
          ];
        }

        const body = JSON.stringify({
          items,
          sections: sectionToBundle,
          sections_url: window.location.pathname,
        });

        this.toggleLoading(true);

        fetch(`${window.FoxThemeSettings.routes.cart_add_url}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
          },
          body,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(
                response.description ||
                  this.strings.add_failed ||
                  "Failed to add items"
              );
              document.dispatchEvent(
                new CustomEvent("product-ajax:error", {
                  detail: { errorMessage: response.description },
                })
              );
              return;
            }

            if (
              !window.FoxThemeSettings.cart.openDrawerWhenAdded ||
              window.FoxThemeSettings.cart.cartType === "page"
            ) {
              window.FoxTheme?.Notification?.show({
                target: this.errorWrapper || document.body,
                method: "appendChild",
                type: "success",
                message: window.FoxThemeStrings?.item_added,
                last: 3000,
              });
            }

            if (window.FoxThemeSettings.cart.cartType === "page") {
              window.FoxThemeCartHelpers?.getState().then((state) => {
                window.FoxThemeCartHelpers.updateCartCount(state.item_count);
              });
            }

            window.FoxThemeEvents?.emit(PUB_SUB_EVENTS.cartUpdate, response);
            document.dispatchEvent(
              new CustomEvent("product-ajax:added", {
                detail: { product: response },
              })
            );
          })
          .catch((err) => {
            this.showError(err && err.toString());
          })
          .finally(() => {
            this.toggleLoading(false);
          });
      }

      showError(message) {
        if (window.FoxTheme?.Notification && this.errorWrapper) {
          window.FoxTheme.Notification.show({
            target: this.errorWrapper,
            method: "appendChild",
            type: "warning",
            message,
            last: 3000,
          });
          return;
        }
        if (this.errorWrapper) this.errorWrapper.textContent = message;
      }

      toggleLoading(loading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle("btn--loading", loading);
        this.submitButton.disabled = loading || this.submitButton.disabled;
        if (!loading) this.updateState();
      }
    }
  );
}
